import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, Send, ArrowLeft, Calendar, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const PRIORITY_COLORS = {
  high: { bg: 'bg-red-50', border: 'border-red-500', text: 'text-red-900', badge: 'bg-red-500' },
  medium: { bg: 'bg-yellow-50', border: 'border-yellow-500', text: 'text-yellow-900', badge: 'bg-yellow-500' },
  low: { bg: 'bg-blue-50', border: 'border-blue-500', text: 'text-blue-900', badge: 'bg-blue-500' }
};

const AnnouncementBuilder = () => {
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showBuilder, setShowBuilder] = useState(false);
  const [currentAnnouncement, setCurrentAnnouncement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('drafts'); // 'drafts' | 'published'
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    priority: 'medium',
    category: '',
    scheduled_for: '',
    expires_at: '',
    target_audience: {
      type: 'whole_company',
      departments: [],
      employee_ids: []
    }
  });

  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  useEffect(() => {
    fetchAnnouncements();
    fetchCategories();
    fetchDepartments();
    fetchEmployees();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/announcements/manager/all`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        cache: 'no-store'
      });
      const data = await response.json();
      setAnnouncements(data);
    } catch (error) {
      console.error('Error fetching announcements:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/announcements/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem('token');
      // Use /api/employees which works for both managers and admins
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/employees`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        console.error('Failed to fetch employees for departments:', response.status);
        return;
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        const uniqueDepts = [...new Set(data.map(emp => emp.department).filter(Boolean))];
        setDepartments(uniqueDepts);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      // Use /api/employees which works for both managers and admins
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/employees`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        console.error('Failed to fetch employees:', response.status);
        return;
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        setEmployees(data);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const saveAnnouncement = async () => {
    if (!formData.title || !formData.content || !formData.category) {
      alert('Please fill in Title, Content, and Category');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = currentAnnouncement
        ? `${process.env.REACT_APP_BACKEND_URL}/api/announcements/${currentAnnouncement.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/announcements/create`;
      
      const method = currentAnnouncement ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert('Announcement saved successfully!');
        fetchAnnouncements();
        fetchCategories(); // Refresh categories in case a new one was added
        resetForm();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error saving announcement:', error);
      alert('Failed to save announcement');
    }
    setLoading(false);
  };

  const publishAnnouncement = async () => {
    if (!formData.title || !formData.content || !formData.category) {
      alert('Please fill in Title, Content, and Category');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // First save/create
      let announcementId = currentAnnouncement?.id;
      
      if (!announcementId) {
        const createResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/announcements/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });
        
        if (!createResponse.ok) {
          const error = await createResponse.json();
          alert(`Error: ${error.detail}`);
          setLoading(false);
          return;
        }
        
        const createData = await createResponse.json();
        announcementId = createData.announcement_id;
      } else {
        // Update existing
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/announcements/${announcementId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });
      }

      // Publish
      const publishResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/announcements/${announcementId}/publish`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (publishResponse.ok) {
        alert('Announcement published successfully!');
        fetchAnnouncements();
        fetchCategories();
        resetForm();
      } else {
        const error = await publishResponse.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error publishing announcement:', error);
      alert('Failed to publish announcement');
    }
    setLoading(false);
  };

  const deleteAnnouncement = async (announcementId) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/announcements/${announcementId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('Announcement deleted successfully');
        fetchAnnouncements();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error deleting announcement:', error);
    }
  };

  const editAnnouncement = async (announcement) => {
    setCurrentAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content,
      priority: announcement.priority,
      category: announcement.category,
      scheduled_for: announcement.scheduled_for || '',
      expires_at: announcement.expires_at || '',
      target_audience: announcement.target_audience
    });
    setShowBuilder(true);
  };

  const resetForm = () => {
    setCurrentAnnouncement(null);
    setFormData({
      title: '',
      content: '',
      priority: 'medium',
      category: '',
      scheduled_for: '',
      expires_at: '',
      target_audience: {
        type: 'whole_company',
        departments: [],
        employee_ids: []
      }
    });
    setShowBuilder(false);
  };

  const draftAnnouncements = announcements.filter(a => a.status === 'draft');
  const publishedAnnouncements = announcements.filter(a => a.status === 'published');

  if (showBuilder) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={resetForm}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Announcements
            </button>
            <h2 className="text-2xl font-bold text-gray-900">
              {currentAnnouncement ? 'Edit Announcement' : 'Create Announcement'}
            </h2>
          </div>

          {/* Title */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Enter announcement title"
            />
          </div>

          {/* Content */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Content <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              rows="6"
              placeholder="Enter announcement content"
            />
          </div>

          {/* Priority and Category */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>

            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.category}
                onChange={(e) => {
                  setFormData({ ...formData, category: e.target.value });
                  setShowCategoryDropdown(true);
                }}
                onFocus={() => setShowCategoryDropdown(true)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Enter or select category"
              />
              {showCategoryDropdown && categories.length > 0 && formData.category && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                  {categories
                    .filter(cat => cat.toLowerCase().includes(formData.category.toLowerCase()))
                    .map(cat => (
                      <button
                        key={cat}
                        onClick={() => {
                          setFormData({ ...formData, category: cat });
                          setShowCategoryDropdown(false);
                        }}
                        className="w-full text-left px-4 py-2 hover:bg-gray-100"
                      >
                        {cat}
                      </button>
                    ))}
                </div>
              )}
            </div>
          </div>

          {/* Schedule and Expiry */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Schedule For (Optional)
              </label>
              <input
                type="datetime-local"
                value={formData.scheduled_for}
                onChange={(e) => setFormData({ ...formData, scheduled_for: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                Expires (Optional)
              </label>
              <input
                type="datetime-local"
                value={formData.expires_at}
                onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Target Audience */}
          <div className="mb-6 p-4 border rounded-lg bg-gray-50">
            <label className="block text-sm font-medium text-gray-700 mb-2">Target Audience</label>
            <select
              value={formData.target_audience.type}
              onChange={(e) => setFormData({
                ...formData,
                target_audience: {
                  ...formData.target_audience,
                  type: e.target.value,
                  departments: [],
                  employee_ids: []
                }
              })}
              className="w-full px-4 py-2 border rounded-lg mb-2"
            >
              <option value="whole_company">Whole Company</option>
              <option value="by_department">By Department</option>
              <option value="individual">Individual Employees</option>
            </select>

            {formData.target_audience.type === 'by_department' && (
              <div className="mt-2">
                <label className="block text-sm text-gray-600 mb-1">Select Departments</label>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {departments.map(dept => (
                    <label key={dept} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.target_audience.departments.includes(dept)}
                        onChange={(e) => {
                          const depts = e.target.checked
                            ? [...formData.target_audience.departments, dept]
                            : formData.target_audience.departments.filter(d => d !== dept);
                          setFormData({
                            ...formData,
                            target_audience: { ...formData.target_audience, departments: depts }
                          });
                        }}
                        className="mr-2"
                      />
                      {dept}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {formData.target_audience.type === 'individual' && (
              <div className="mt-2">
                <label className="block text-sm text-gray-600 mb-1">Select Employees</label>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {employees.map(emp => (
                    <label key={emp.id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.target_audience.employee_ids.includes(emp.id)}
                        onChange={(e) => {
                          const ids = e.target.checked
                            ? [...formData.target_audience.employee_ids, emp.id]
                            : formData.target_audience.employee_ids.filter(id => id !== emp.id);
                          setFormData({
                            ...formData,
                            target_audience: { ...formData.target_audience, employee_ids: ids }
                          });
                        }}
                        className="mr-2"
                      />
                      {emp.first_name} {emp.last_name}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              onClick={resetForm}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={saveAnnouncement}
              disabled={loading}
              className="flex items-center px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <Save className="w-4 h-4 mr-2" />
              Save as Draft
            </button>
            <button
              onClick={publishAnnouncement}
              disabled={loading}
              className="flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              <Send className="w-4 h-4 mr-2" />
              Publish
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Announcement List View
  return (
    <div className="space-y-6 overflow-x-hidden">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col gap-4">
          <Button onClick={() => navigate('/admin/dashboard')} variant="ghost" size="sm" className="self-start">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Announcements</h1>
            <p className="text-sm text-gray-500 mt-1">Create and manage company announcements</p>
          </div>
          <button
            onClick={() => setShowBuilder(true)}
            className="flex items-center justify-center px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 w-full sm:w-auto"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create Announcement
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <div className="flex space-x-8">
          <button
            onClick={() => setActiveTab('drafts')}
            className={`pb-3 px-1 ${
              activeTab === 'drafts'
                ? 'border-b-2 border-blue-600 text-blue-600 font-semibold'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Drafts ({draftAnnouncements.length})
          </button>
          <button
            onClick={() => setActiveTab('published')}
            className={`pb-3 px-1 ${
              activeTab === 'published'
                ? 'border-b-2 border-blue-600 text-blue-600 font-semibold'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Published ({publishedAnnouncements.length})
          </button>
        </div>
      </div>

      {/* Announcement Cards */}
      <div className="grid gap-4">
        {(activeTab === 'drafts' ? draftAnnouncements : publishedAnnouncements).map(announcement => {
          const priorityStyle = PRIORITY_COLORS[announcement.priority];
          return (
            <div key={announcement.id} className={`${priorityStyle.bg} border-l-4 ${priorityStyle.border} rounded-lg p-6`}>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className={`text-xl font-semibold ${priorityStyle.text}`}>{announcement.title}</h3>
                    <span className={`${priorityStyle.badge} text-white text-xs px-2 py-1 rounded-full uppercase`}>
                      {announcement.priority}
                    </span>
                    <span className="bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded-full">
                      {announcement.category}
                    </span>
                  </div>
                  <p className={`${priorityStyle.text} mb-3 line-clamp-2`}>{announcement.content}</p>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>Target: {announcement.target_audience.type.replace('_', ' ')}</p>
                    {announcement.scheduled_for && (
                      <p>Scheduled: {new Date(announcement.scheduled_for).toLocaleString()}</p>
                    )}
                    {announcement.expires_at && (
                      <p>Expires: {new Date(announcement.expires_at).toLocaleString()}</p>
                    )}
                    {announcement.published_at && (
                      <p>Published: {new Date(announcement.published_at).toLocaleDateString()}</p>
                    )}
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => editAnnouncement(announcement)}
                    className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                  >
                    {announcement.status === 'draft' ? 'Edit' : 'View'}
                  </button>
                  <button
                    onClick={() => deleteAnnouncement(announcement.id)}
                    className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-1"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {(activeTab === 'drafts' ? draftAnnouncements : publishedAnnouncements).length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg">
            <p className="text-gray-500">
              No {activeTab} announcements yet.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnouncementBuilder;
